import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { EntityState } from 'breeze-client';
import { firstValueFrom } from 'rxjs';
import { environment } from 'environments/environment';
import { CreditMemo, CreditMemoStatusEnum, CreditMemoTypeEnum, Product, ReturnRequestDetail } from '../model/entities/entity-model';
import { DialogBaseComponent, DialogParams } from '../shared/dialog-base.component';
import { DomainService } from '../shared/domain.service';
import { QbPostResult } from '../shared/qb-post-result';

export interface SettleRrdDialogParams extends DialogParams {
  rrdId: number;
}

interface ProposedSettlement {
  CreditAmt: number;
  GrossCreditAmt: number;
  RestockFee: number;
  CreditMemoType: string | null;
  IsZero: boolean;
  UnreconcilableReason: string | null;
  // tolerate camelCase from possible alt JSON config
  creditAmt?: number;
  grossCreditAmt?: number;
  restockFee?: number;
  creditMemoType?: string | null;
  isZero?: boolean;
  unreconcilableReason?: string | null;
}

/**
 * Pass 4 dialog for the SETTLING phase. Three visually distinct branches
 * driven by the server-computed ProposedSettlement:
 *
 *   1. IsZero            — RestockToAccountOwned with no fee. Read-only
 *                          "$0 settlement" summary; one Confirm button.
 *                          No CreditMemo is created.
 *
 *   2. CreditAmt > 0     — AcceptForCredit. Shows gross + restock + net.
 *                          Operator confirms; client creates one CreditMemo
 *                          via Breeze (matching v1's createCreditMemo
 *                          pattern) and saves before calling
 *                          RecordSettlement.
 *
 *   3. UnreconcilableReason — pre-filled ReconciliationExplanation
 *                          textarea; "Move to UNRECONCILED" button calls
 *                          RecordUnreconciliation.
 */
@Component({
  selector: 'app-settle-rrd-dialog',
  templateUrl: './settle-rrd-dialog.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SettleRrdDialogComponent extends DialogBaseComponent<SettleRrdDialogParams> implements OnInit {
  rrd: ReturnRequestDetail | null = null;
  product: Product | null = null;
  proposal: {
    creditAmt: number;
    grossCreditAmt: number;
    restockFee: number;
    creditMemoType: string | null;
    isZero: boolean;
    unreconcilableReason: string | null;
  } | null = null;
  submitting = false;
  errorMessage: string | null = null;
  loadError: string | null = null;

  /** Operator-editable seed/explanation for the UNRECONCILED branch. */
  reconciliationExplanation = '';

  constructor(
    config: DynamicDialogConfig<SettleRrdDialogParams>,
    dialogRef: DynamicDialogRef,
    domainService: DomainService,
    private http: HttpClient,
  ) {
    super(config, dialogRef, domainService);
  }

  async ngOnInit() {
    const r = await this.uow.fetchEntityByKey(ReturnRequestDetail, this.params.rrdId);
    this.rrd = (r?.entity as ReturnRequestDetail) ?? null;
    if (this.rrd?.joDetail) {
      // Load the Product with manufacturer + featureChoices so the template can
      // render the full identification (manufacturer / style / features) instead
      // of the opaque productTypeConfigName.
      this.product = await this.dbQueryService.getProductById(this.rrd.joDetail.productId);
    }
    if (this.rrd) {
      try {
        const url = environment.apiRoot + 'breeze/Uniformax/ComputeProposedSettlement';
        const params = new HttpParams().set('rrdId', String(this.rrd.id));
        const p = await firstValueFrom(this.http.get<ProposedSettlement>(url, { params }));
        // Server may return either casing depending on serializer config; accept both.
        this.proposal = {
          creditAmt: p.CreditAmt ?? p.creditAmt ?? 0,
          grossCreditAmt: p.GrossCreditAmt ?? p.grossCreditAmt ?? 0,
          restockFee: p.RestockFee ?? p.restockFee ?? 0,
          creditMemoType: p.CreditMemoType ?? p.creditMemoType ?? null,
          isZero: p.IsZero ?? p.isZero ?? false,
          unreconcilableReason: p.UnreconcilableReason ?? p.unreconcilableReason ?? null,
        };
        if (this.proposal.unreconcilableReason) {
          this.reconciliationExplanation = this.proposal.unreconcilableReason;
        }
      } catch (err: any) {
        this.loadError = err?.error?.error || err?.message || 'Failed to compute settlement.';
      }
    }
    this.isPageReady = true;
  }

  get variantText(): string {
    if (!this.product) return '';
    return (this.product as any).getFeatureChoicesExtract?.() ?? '';
  }

  get isCreditBranch(): boolean {
    return !!this.proposal && !this.proposal.isZero && !this.proposal.unreconcilableReason && this.proposal.creditAmt > 0;
  }

  get isZeroBranch(): boolean {
    return !!this.proposal && this.proposal.isZero;
  }

  get isUnreconcilableBranch(): boolean {
    return !!this.proposal && !!this.proposal.unreconcilableReason;
  }

  get canSubmit(): boolean {
    if (this.submitting || !this.rrd || !this.proposal) return false;
    if (this.isUnreconcilableBranch) {
      return this.reconciliationExplanation.trim().length > 0;
    }
    return true;
  }

  get confirmLabel(): string {
    if (this.isUnreconcilableBranch) return 'Move to UNRECONCILED';
    return 'Confirm Settlement';
  }

  get transactionTypeLabel(): string {
    if (this.proposal?.creditMemoType === 'PcardRefund') return 'P-Card Refund';
    if (this.proposal?.creditMemoType === 'Memo') return 'Credit Memo';
    return this.proposal?.creditMemoType ?? '';
  }

  async confirm() {
    if (!this.canSubmit || !this.rrd || !this.proposal) return;
    this.submitting = true;
    this.errorMessage = null;

    try {
      const actor = this.authService.getUser()?.initials ?? 'SYS';
      let createdCmId: number | null = null;

      if (this.isUnreconcilableBranch) {
        await this.callUnreconciliation(actor);
      } else if (this.isZeroBranch) {
        await this.callRecordSettlement(actor, null);
      } else {
        createdCmId = await this.createAndSaveCreditMemo();
        await this.callRecordSettlement(actor, createdCmId);
      }

      const phaseLabel = this.isUnreconcilableBranch ? 'UNRECONCILED' : 'CLOSED';
      this.toastr.success(`RRD #${this.rrd.id} — moved to ${phaseLabel}`);

      // Post the new CreditMemo to QBO inline so the operator gets immediate feedback.
      // We do this AFTER the phase advance: settlement is already recorded, so a QBO
      // failure leaves the RRD CLOSED and the CM at Unposted — matching v1's
      // `postOrReject` behavior where return processing isn't blocked by QBO outages.
      if (createdCmId != null) {
        await this.postCreditMemoToQbo(createdCmId);
      }

      this.dialogRef.close(true);
    } catch (err: any) {
      const msg = err?.error?.error || err?.message || 'Failed to record settlement.';
      this.errorMessage = msg;
    } finally {
      this.submitting = false;
    }
  }

  /** Post the newly-created CreditMemo to QBO and toast the outcome. Failure does
   *  NOT throw — the settlement is already done; QBO can catch up later. */
  private async postCreditMemoToQbo(cmId: number) {
    const url = environment.apiRoot + `api/quickbooks/PostCreditMemoToGL?creditMemoId=${cmId}`;
    try {
      const result = await firstValueFrom(this.http.post<QbPostResult>(url, {}));
      const posted = result?.PostedIds ?? [];
      const failed = result?.Failed ?? [];

      // (Intentionally NOT refreshing the CreditMemo from Breeze — there's no
      //  Breeze IQueryable<CreditMemo> exposed on the server, so fetchEntityByKey
      //  would 404. The dialog is closing immediately; the next page load that
      //  queries CMs will pick up the new status / GLCreditMemoId.)

      if (failed.length > 0 || posted.length === 0) {
        const firstMsg =
          failed[0]?.ErrorMessage || 'QuickBooks did not post the credit memo. Check the QuickBooks Issues page or the server log.';
        this.toastr.error(`Credit memo ${cmId} was created but failed to post to QuickBooks: ${firstMsg}`, 'QuickBooks');
        return;
      }

      this.toastr.success(`Posted credit memo ${cmId} to QuickBooks`, 'QuickBooks');
    } catch (err: any) {
      this.toastr.error(`Credit memo ${cmId} was created but the QuickBooks post failed: ${err?.message || 'Unknown error'}`, 'QuickBooks');
    }
  }

  /** Create one CreditMemo for the proposed amount via Breeze and save.
   * Mirrors return-request-detail-dialog.component.ts:394 createCreditMemo
   * (the v1 pattern). Returns the new CreditMemo's id for server validation. */
  private async createAndSaveCreditMemo(): Promise<number> {
    if (!this.rrd || !this.proposal) throw new Error('Cannot create CreditMemo without an RRD/proposal.');
    const typeId = this.proposal.creditMemoType === 'PcardRefund' ? CreditMemoTypeEnum.PcardRefund : CreditMemoTypeEnum.Memo;

    const cmStruct = {} as CreditMemo;
    cmStruct.joHeaderId = this.rrd.returnRequest.joHeaderId;
    cmStruct.returnRequestDetailId = this.rrd.id;
    cmStruct.creditMemoTypeId = typeId;
    cmStruct.creditMemoAmt = this.proposal.creditAmt;
    cmStruct.wasAmtOverridden = false;
    cmStruct.creditMemoStatusId = CreditMemoStatusEnum.Unposted;

    if (typeId === CreditMemoTypeEnum.PcardRefund) {
      cmStruct.transactionId = await this.resolvePcardTransactionId();
    }

    const cm = this.uow.createEntity(CreditMemo, cmStruct, EntityState.Added);
    const saved = await this.dbSaveService.saveChanges();
    if (!saved) {
      throw new Error('Validation failed while saving the CreditMemo.');
    }
    return cm.id;
  }

  /** Find the Authorize.Net transaction id that paid for this RRD's product.
   * Walks the JO's invoices and returns the pCardTransactionId from whichever
   * one shipped the JoDetail. Throws if no charged p-card invoice is found —
   * a PcardRefund CM with no transactionId would silently fail at QB-post time. */
  private async resolvePcardTransactionId(): Promise<string> {
    const joHeaderId = this.rrd!.returnRequest.joHeaderId;
    const joDetailId = this.rrd!.joDetailId;
    const invoiceHeaders = await this.dbQueryService.getInvoiceHeadersByJoHeaderId(joHeaderId);
    const match = invoiceHeaders.find((ih) => !!ih.pCardTransactionId && ih.invoiceDetails.some((d) => d.joDetailId === joDetailId));
    if (!match?.pCardTransactionId) {
      throw new Error(
        `Cannot issue P-Card Refund for RRD #${this.rrd!.id}: no invoice on JO ${joHeaderId} ` +
          `with a p-card charge covers JoDetail ${joDetailId}. The p-card may not have been charged yet.`,
      );
    }
    return match.pCardTransactionId;
  }

  private async callRecordSettlement(actor: string, creditMemoId: number | null) {
    const url = environment.apiRoot + 'breeze/Uniformax/RecordSettlement';
    await firstValueFrom(
      this.http.post(url, {
        rrdId: this.rrd!.id,
        creditMemoId: creditMemoId,
        actor: actor,
      }),
    );
  }

  private async callUnreconciliation(actor: string) {
    const url = environment.apiRoot + 'breeze/Uniformax/RecordUnreconciliation';
    await firstValueFrom(
      this.http.post(url, {
        rrdId: this.rrd!.id,
        explanation: this.reconciliationExplanation.trim(),
        actor: actor,
      }),
    );
  }

  cancel() {
    if (this.dbSaveService.hasChanges()) {
      this.dbSaveService.rejectChanges();
    }
    this.dialogRef.close(false);
  }
}
